import type { AttachmentCategory } from './attachmentTypes';

/**
 * Format bytes into human-readable binary file sizes (KB, MB, GB)
 */
export function formatFileSize(bytes?: number): string {
  if (bytes === undefined || bytes === null || isNaN(bytes) || bytes < 0) {
    return '';
  }
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/**
 * Detect file classification based on MIME type and file extension
 */
export function getAttachmentCategory(
  fileName: string,
  fileType?: string
): AttachmentCategory {
  const cleanType = (fileType || '').toLowerCase().trim();
  const cleanName = (fileName || '').toLowerCase().trim();
  const extension = cleanName.includes('.') ? cleanName.split('.').pop() || '' : '';

  // 1. IMAGES
  if (
    cleanType.startsWith('image/') ||
    ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'ico', 'heic'].includes(extension)
  ) {
    // Treat SVG as document for security (prevents XSS)
    if (cleanType === 'image/svg+xml' || extension === 'svg') {
      return 'DOCUMENT';
    }
    return 'IMAGE';
  }

  // 2. VIDEOS
  if (
    cleanType.startsWith('video/') ||
    ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv', 'm4v', '3gp'].includes(extension)
  ) {
    return 'VIDEO';
  }

  // 3. AUDIO
  if (
    cleanType.startsWith('audio/') ||
    ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac', 'opus', 'wma'].includes(extension)
  ) {
    return 'AUDIO';
  }

  // 4. PDF
  if (cleanType === 'application/pdf' || extension === 'pdf') {
    return 'PDF';
  }

  // 5. SPREADSHEET
  if (
    cleanType.includes('excel') ||
    cleanType.includes('spreadsheet') ||
    cleanType === 'text/csv' ||
    ['xls', 'xlsx', 'csv', 'ods'].includes(extension)
  ) {
    return 'SPREADSHEET';
  }

  // 6. PRESENTATION
  if (
    cleanType.includes('powerpoint') ||
    cleanType.includes('presentation') ||
    ['ppt', 'pptx', 'odp'].includes(extension)
  ) {
    return 'PRESENTATION';
  }

  // 7. DOCUMENT
  if (
    cleanType.includes('word') ||
    cleanType.includes('document') ||
    cleanType === 'text/plain' ||
    cleanType === 'text/markdown' ||
    cleanType === 'application/rtf' ||
    ['doc', 'docx', 'txt', 'rtf', 'odt', 'md', 'json', 'log'].includes(extension)
  ) {
    return 'DOCUMENT';
  }

  // 8. ARCHIVE
  if (
    cleanType.includes('zip') ||
    cleanType.includes('compressed') ||
    cleanType.includes('tar') ||
    cleanType.includes('archive') ||
    ['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz', 'tgz'].includes(extension)
  ) {
    return 'ARCHIVE';
  }

  return 'GENERIC';
}
